import { useCallback, useEffect, useRef, useState } from "react";
import AdminApp from "../admin/AdminApp";
import GameShell from "../game/GameShell";
import { getGameLocation } from "../game/data/gameLocations";
import ExperienceOverlay, {
  loadCommunity,
  saveCommunity,
} from "../game/presentation/components/ExperienceOverlay";
import AchievementsBar from "../game/presentation/components/AchievementsBar";
import {
  clearPlayerSession,
  fetchSessionPlayer,
  loadPlayerSession,
  restorePlayerSession,
  savePlayerSession,
  verifyLoginToken,
} from "../game/infrastructure/playerAuthApi";
import devilIllustration from "../../assets/diablo.webp";
import { preloadMapAmbientMusic, startMapAmbientMusic } from "../game/infrastructure/mapAmbientMusic";
import { playEntryRevealAudio, preloadEntryRevealAudio } from "../game/infrastructure/entryRevealAudio";
import { clearPendingTournamentToken, savePendingTournamentToken, sceneLocationIdForZone } from "../game/infrastructure/tournamentLinks";
import useLocatePlayer from "./useLocatePlayer";

const routeToScreen = {
  "/": "onboarding",
  "/inicio": "hub",
  "/ubicacion": "locating",
  "/vs": "vs-setup",
  "/ranking": "ranking",
  "/pendientes": "pending",
  "/resultado": "results",
  "/reto": "challenge",
  "/torneo": "tournament",
  "/creditos": "credits",
  "/review": "review",
};

const GAME_PATH = "/juego";
const ADMIN_PATH = "/admin";
// Sin sesión solo existe el inicio (correo/validación) y el admin (con su
// propia clave). Todo lo demás exige jugador validado.
const PROTECTED_PATHS = [
  "/inicio",
  "/ubicacion",
  "/vs",
  "/ranking",
  "/pendientes",
  "/resultado",
  "/reto",
  "/torneo",
  "/creditos",
  "/review",
  GAME_PATH,
];

function initialAuthState() {
  try {
    if (new URLSearchParams(window.location.search).get("loginToken")) return "checking";
    if (window.location.pathname === ADMIN_PATH) return "guest";
    return localStorage.getItem("nacanaca-session") ? "checking" : "guest";
  } catch {
    return "guest";
  }
}

// Tras entrar, las invitaciones pendientes (duelo o torneo) tienen prioridad
// sobre el mapa general.
function pathAfterLogin() {
  try {
    if (localStorage.getItem("nacanaca-pending-challenge")) return "/reto";
    if (localStorage.getItem("nacanaca-pending-tournament")) return "/torneo";
  } catch {
    // Sin almacenamiento: al mapa general.
  }
  return "/inicio";
}

export default function PortalApp() {
  const [path, setPath] = useState(window.location.pathname);
  const [user, setUser] = useState(
    () => localStorage.getItem("nacanaca-player") || "",
  );
  const [zone, setZone] = useState(
    () => sessionStorage.getItem("nacanaca-zone") || "",
  );
  // Fijación GPS de la sesión: en qué ubicación del mapa general está el
  // jugador (solo las 5 zonas del mapa general participan). El pin del mapa
  // solo se muestra cuando `status === 'inside'`.
  const [gps, setGps] = useState(() => {
    try {
      return JSON.parse(sessionStorage.getItem("nacanaca-gps") || "null");
    } catch {
      return null;
    }
  });
  const gpsRequestRef = useRef(0);
  const gpsCoordsRef = useRef(null);
  const autoGpsRequestedRef = useRef(false);
  const [isEntryRevealVisible, setIsEntryRevealVisible] = useState(false);
  const [isEntryRevealAnimating, setIsEntryRevealAnimating] = useState(false);
  const entryRevealStartedRef = useRef(false);
  const entryRevealImageRef = useRef(null);
  const [magicStatus, setMagicStatus] = useState({ state: "idle", message: "" });
  const [authState, setAuthState] = useState(initialAuthState);
  const triggerEntryReveal = useCallback(() => {
    // El overlay queda detenido en el primer frame hasta que la imagen esté
    // lista; así el audio y la aparición del diablito comparten el disparo.
    entryRevealStartedRef.current = false;
    setIsEntryRevealAnimating(false);
    setIsEntryRevealVisible(true);
  }, []);
  const startEntryRevealAnimation = useCallback(() => {
    if (entryRevealStartedRef.current) return;
    entryRevealStartedRef.current = true;
    playEntryRevealAudio();
    setIsEntryRevealAnimating(true);
  }, []);

  // Una invitación sobrevive el registro o acceso mágico, pero el bearer se
  // retira de la URL inmediatamente. Se elimina de localStorage al reclamarlo.
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const token = window.location.pathname === '/reto'
      ? params.get('token')
      : params.get('reto');
    if (!token) return;
    localStorage.setItem('nacanaca-pending-challenge', token);
    const cleanPath = authState === 'authed' ? '/reto' : '/';
    window.history.replaceState({}, '', cleanPath);
    setPath(cleanPath);
  }, [authState]);

  // Una invitación de torneo sobrevive igual: el token se guarda y la URL se
  // limpia de inmediato. Se elimina al unirse al torneo.
  useEffect(() => {
    if (window.location.pathname !== '/torneo') return;
    const token = new URLSearchParams(window.location.search).get('token');
    if (!token) return;
    savePendingTournamentToken(token);
    const cleanPath = authState === 'authed' ? '/torneo' : '/';
    window.history.replaceState({}, '', cleanPath);
    setPath(cleanPath);
  }, [authState]);

  // La música del mapa se empieza a descargar desde el arranque,
  // mientras el jugador está en el login, para que suene al instante al llegar.
  useEffect(() => {
    preloadMapAmbientMusic();
    preloadEntryRevealAudio();
    const image = new Image();
    image.decoding = "async";
    image.src = devilIllustration;
    if (typeof image.decode === "function") image.decode().catch(() => {});
  }, []);

  // Sesión recordada: si ya entró una vez en este navegador, pasa directo
  // al mapa sin pedir correo, código ni instrucciones.
  useEffect(() => {
    if (new URLSearchParams(window.location.search).get("loginToken")) return;
    if (window.location.pathname === ADMIN_PATH) return;
    let stored;
    try {
      stored = loadPlayerSession();
    } catch {
      setAuthState("guest");
      return;
    }
    if (!stored.sessionToken) {
      setAuthState("guest");
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const me = await fetchSessionPlayer(stored.sessionToken);
        if (cancelled) return;
        savePlayerSession({ sessionToken: stored.sessionToken, player: me.player });
        localStorage.setItem("nacanaca-player", me.player.username);
        setUser(me.player.username);
        setAuthState("authed");
        // Sesión recordada: arranca la música del mapa al instante,
        // sin esperar al montaje del hub ni al iframe.
        startMapAmbientMusic();
        if (window.location.pathname === "/") navigate(pathAfterLogin());
      } catch (firstError) {
        if (!cancelled && firstError.status && stored.player?.email) {
          try {
            const restored = await restorePlayerSession(stored.player.email, stored.sessionToken);
            if (cancelled) return;
            savePlayerSession(restored);
            localStorage.setItem("nacanaca-player", restored.player.username);
            setUser(restored.player.username);
            setAuthState("authed");
            startMapAmbientMusic();
            if (window.location.pathname === "/") navigate(pathAfterLogin());
            return;
          } catch {
            // Sin acceso recordado válido: cae al inicio normal.
          }
        }
        if (!cancelled) setAuthState("guest");
      }
    })();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Guard: sin sesión, cualquier URL protegida vuelve al inicio (login).
  useEffect(() => {
    if (path === ADMIN_PATH) return;
    if (authState === "guest" && PROTECTED_PATHS.includes(path)) {
      window.history.replaceState({}, "", "/");
      setPath("/");
    }
  }, [path, authState]);

  // Enlace mágico: /?loginToken=... (enviado por correo a usuarios existentes).
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const token = params.get("loginToken");
    if (!token) return;
    // Limpia el token de la URL de inmediato para no dejarlo en el historial.
    const cleanUrl = `${window.location.pathname}`;
    window.history.replaceState({}, "", cleanUrl);
    setMagicStatus({ state: "verifying", message: "Validando tu enlace de acceso…" });
    verifyLoginToken(token)
      .then((result) => {
        savePlayerSession(result);
        localStorage.setItem("nacanaca-player", result.player.username);
        setUser(result.player.username);
        setAuthState("authed");
        triggerEntryReveal();
        setMagicStatus({ state: "done", message: "" });
        // Enlace mágico: la música del mapa suena al instante del login.
        startMapAmbientMusic();
        navigate(pathAfterLogin());
      })
      .catch((error) => {
        setAuthState("guest");
        setMagicStatus({
          state: "error",
          message: error.message || "El enlace es inválido o expiró. Pide uno nuevo desde tu correo.",
        });
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const handlePopState = () => setPath(window.location.pathname);
    window.addEventListener("popstate", handlePopState);
    return () => window.removeEventListener("popstate", handlePopState);
  }, []);

  useEffect(() => {
    if (!isEntryRevealVisible || !isEntryRevealAnimating) return undefined;
    const timeoutId = window.setTimeout(
      () => setIsEntryRevealVisible(false),
      1900,
    );
    return () => window.clearTimeout(timeoutId);
  }, [isEntryRevealVisible, isEntryRevealAnimating]);

  useEffect(() => {
    if (!isEntryRevealVisible) return undefined;
    const image = entryRevealImageRef.current;
    if (image?.complete) startEntryRevealAnimation();
    return undefined;
  }, [isEntryRevealVisible, startEntryRevealAnimation]);

  const navigate = useCallback((nextPath) => {
    const nextUrl = new URL(nextPath, window.location.origin);
    window.history.pushState(
      {},
      "",
      `${nextUrl.pathname}${nextUrl.search}${nextUrl.hash}`,
    );
    setPath(nextUrl.pathname);
  }, []);
  const navigateScreen = useCallback(
    (screen) => {
      if (screen === 'hub') {
        // Las invitaciones solo tienen prioridad al terminar el login.
        // Desde el torneo, "Volver al mapa" debe salir aunque haya un token pendiente.
        if (path === '/') {
          navigate(pathAfterLogin());
          return;
        }
        clearPendingTournamentToken();
      }
      navigate(
        {
          onboarding: "/",
          hub: "/inicio",
          locating: "/ubicacion",
          "vs-setup": "/vs",
          ranking: "/ranking",
          pending: "/pendientes",
          results: "/resultado",
          challenge: "/reto",
          tournament: "/torneo",
          credits: "/creditos",
          review: "/review",
        }[screen] || "/inicio",
      );
    },
    [navigate, path],
  );
  const saveUser = useCallback((name) => {
    localStorage.setItem("nacanaca-player", name);
    setUser(name);
    setAuthState("authed");
    triggerEntryReveal();
    // Login normal (código / pase directo): dispara la música del mapa en
    // el mismo flujo del gesto para que suene al instante, sin esperar
    // al montaje del hub. Si el autoplay sigue bloqueado, MapAmbientAudio
    // lo reintentará con el siguiente toque/tecla.
    startMapAmbientMusic();
  }, [triggerEntryReveal]);
  const handleLogout = useCallback(() => {
    if (!window.confirm("¿Cerrar sesión en este dispositivo?")) return;
    clearPlayerSession();
    sessionStorage.removeItem("nacanaca-zone");
    sessionStorage.removeItem('nacanaca-scene-location');
    sessionStorage.removeItem("nacanaca-gps");
    sessionStorage.removeItem("nacanaca-result");
    sessionStorage.removeItem("nacanaca-active-challenge");
    sessionStorage.removeItem("nacanaca-active-tournament");
    try { localStorage.removeItem("nacanaca-pending-tournament"); } catch { /* sin almacenamiento */ }
    setUser("");
    setZone("");
    setGps(null);
    gpsCoordsRef.current = null;
    autoGpsRequestedRef.current = false;
    setAuthState("guest");
    navigate("/");
  }, [navigate]);
  // La resolución y persistencia del GPS viven en un módulo independiente.
  const locate = useLocatePlayer({ setZone, setGps, gpsRequestRef, gpsCoordsRef });
  // Al abrir el mapa general (/inicio) se pide el GPS una vez por sesión
  // para poder mostrar el pin "dónde estoy". Solo auto-dispara si aún no
  // hay fijación (gps === null). Una fijación `inside` restaurada desde la
  // sesión no trae coordenadas en memoria, así que se valida una vez más
  // antes de permitir que una partida la use. Si el usuario denegó o falló,
  // no se re-pregunta en bucle: el botón del mapa reintenta.
  useEffect(() => {
    if (path !== "/inicio") return;
    if (authState !== "authed") return;
    const needsFreshInsideFix = gps?.status === 'inside' && !gpsCoordsRef.current;
    if (gps !== null && !needsFreshInsideFix) return;
    if (autoGpsRequestedRef.current) return;
    autoGpsRequestedRef.current = true;
    locate();
  }, [path, authState, gps, locate]);
  // Maldecir entra directo al juego: autostart=1 salta el menú intermedio.
  const start = useCallback((mode, options = {}) => {
    // El torneo fija zona y ambientación exacta: todos juegan igual.
    if (mode === 'tournament' && options.tournament) {
      const tournament = options.tournament;
      sessionStorage.setItem('nacanaca-zone', tournament.settings.zone);
      setZone(tournament.settings.zone);
      sessionStorage.setItem(
        'nacanaca-scene-location',
        tournament.settings.sceneLocationId || sceneLocationIdForZone(tournament.settings.zone),
      );
      sessionStorage.setItem('nacanaca-active-tournament', JSON.stringify({
        id: tournament.id, token: tournament.token, tournament,
      }));
      sessionStorage.removeItem('nacanaca-active-challenge');
      navigate(`${GAME_PATH}?modo=tournament&autostart=1`);
      return;
    }
    // El GPS es opcional en todos los modos. Las pantallas previas ofrecen
    // solicitarlo, pero nunca frenan el acceso al juego si no está disponible.
    if (!sessionStorage.getItem('nacanaca-zone')) {
      sessionStorage.setItem('nacanaca-zone', 'Toluca');
      setZone('Toluca');
    }
    if (!sessionStorage.getItem('nacanaca-scene-location')) {
      sessionStorage.setItem('nacanaca-scene-location', 'centro');
    }
    const active = options.challengeToken || options.challengeId
      ? { token: options.challengeToken, id: options.challengeId || options.challenge?.id, challenge: options.challenge }
      : null;
    if (active) sessionStorage.setItem('nacanaca-active-challenge', JSON.stringify(active));
    else sessionStorage.removeItem('nacanaca-active-challenge');
    sessionStorage.removeItem('nacanaca-active-tournament');
    navigate(`${GAME_PATH}?modo=${encodeURIComponent(mode)}&autostart=1`);
  }, [navigate]);
  const complete = useCallback(
    (stats, details) => {
      const community = loadCommunity();
      const play = {
        user: details.user,
        zone: details.zone,
        score: stats.score,
        mode: details.mode,
        at: Date.now(),
      };
      community.plays = [...community.plays, play].slice(-150);
      const activeChallenge = JSON.parse(
        sessionStorage.getItem("nacanaca-active-challenge") || "null",
      );
      // El vínculo único del duelo identifica a la persona retada: cada
      // challengeId distinto en modo `vs` suma un rival en el Salón.
      // El puntaje oficial ya lo registró GameShell vía VerifiedPlaySession;
      // aquí solo se resuelve el duelo local (primer/segundo turno).
      const challengeId = details.mode === "vs" ? activeChallenge?.id || "" : "";
      let resultDuel = null;
      if (details.mode === "vs") {
        const awaitingIndex = challengeId
          ? community.duels.findIndex(
              (duel) =>
                duel.challengeId === challengeId && !duel.opponent,
            )
          : -1;
        if (awaitingIndex >= 0) {
          // Segundo turno en este navegador: se conoce al ganador.
          const awaiting = community.duels[awaitingIndex];
          const completed = {
            ...awaiting,
            opponent: {
              user: details.user,
              score: Math.floor(stats.score) || 0,
              at: Date.now(),
            },
            status: "completed",
          };
          community.duels[awaitingIndex] = completed;
          resultDuel = completed;
        } else {
          // Primer turno: queda en espera del rival.
          resultDuel = {
            ...play,
            challengeId: challengeId || null,
            expiresAt: activeChallenge?.expiresAt || null,
            opponent: null,
            status: "awaiting",
          };
          community.duels = [...community.duels, resultDuel].slice(-50);
        }
        // El Salón se recalcula en servidor: avisa para re-sincronizar medallas.
        window.dispatchEvent(new CustomEvent('nacanaca-achievements-sync'));
      }
      sessionStorage.removeItem("nacanaca-active-challenge");
      sessionStorage.removeItem("nacanaca-active-tournament");
      saveCommunity(community);
      sessionStorage.setItem(
        "nacanaca-result",
        JSON.stringify({
          stats,
          mode: details.mode,
          zone: details.zone,
          duel: resultDuel,
        }),
      );
      navigate("/resultado");
    },
    [navigate],
  );
  const share = useCallback(async () => {
    const result = JSON.parse(
      sessionStorage.getItem("nacanaca-result") || "{}",
    );
    const text =
      result.mode === "vs"
        ? `¡@${user} te desafía a superar ${result.stats?.score || 0} puntos en La Maldición del Ñaca Ñaca!`
        : `¡Maldecí ${result.zone || "Toluca"} con ${result.stats?.score || 0} puntos!`;
    try {
      if (navigator.share)
        await navigator.share({
          title: "La Maldición del Ñaca Ñaca",
          text,
          url: window.location.href,
        });
      else
        await navigator.clipboard?.writeText(`${text} ${window.location.href}`);
    } catch (error) {
      if (error?.name !== "AbortError")
        console.warn("No se pudo compartir.", error);
    }
  }, [user]);

  if (path === ADMIN_PATH) return <AdminApp />;
  const savedResult = JSON.parse(
    sessionStorage.getItem("nacanaca-result") || "null",
  );
  const screen = routeToScreen[path] || "onboarding";
  if (authState === "checking" && magicStatus.state !== "verifying") {
    return (
      <div className="grid min-h-[100dvh] place-items-center bg-black px-6 text-center">
        <div>
          <p className="text-[10px] font-black uppercase tracking-[.25em] text-[#7EC54A]">Reconociéndote</p>
          <p className="mt-3 font-display text-3xl uppercase text-white">Entrando al mapa…</p>
        </div>
      </div>
    );
  }
  // Sin sesión no se muestra nada protegido; el guard ya redirige al inicio.
  if (authState !== "authed" && PROTECTED_PATHS.includes(path)) return null;
  if (path === GAME_PATH) return <GameShell onComplete={complete} locationCoords={gpsCoordsRef.current} />;
  return (
    <div className="relative min-h-[100dvh] bg-black">
      {magicStatus.state === "verifying" && (
        <div className="fixed inset-x-0 top-0 z-[90] bg-[#7C3AED] px-4 py-3 text-center text-xs font-black uppercase tracking-wider text-white" role="status">
          {magicStatus.message}
        </div>
      )}
      {magicStatus.state === "error" && (
        <div className="fixed inset-x-0 top-0 z-[90] border-b border-[#F7E04B]/40 bg-[#2b1219] px-4 py-3 text-center text-xs font-bold text-[#ffd8de]" role="alert">
          {magicStatus.message}
        </div>
      )}
      {screen === "hub" && (
        <AchievementsBar playerName={user || "Invitado"} mapMode />
      )}
      <ExperienceOverlay
        screen={screen}
        user={user}
        location={zone}
        gps={gps}
        stats={savedResult?.stats}
        mode={savedResult?.mode || "solo"}
        duel={savedResult?.duel || null}
        onUser={saveUser}
        onNavigate={navigateScreen}
        onLocate={locate}
        onStart={start}
        onShare={share}
        onLogout={handleLogout}
        onZoneSelect={(nextZone, locationId) => {
          const selected = getGameLocation(locationId);
          const gameZone = nextZone || selected.gameZone;
          sessionStorage.setItem("nacanaca-zone", gameZone);
          sessionStorage.setItem('nacanaca-scene-location', selected.id);
          setZone(gameZone);
        }}
      />
      {isEntryRevealVisible && (
        <div
          className="entry-reveal fixed inset-0 z-[100] grid place-items-center overflow-hidden bg-[#0A1A0F]"
          style={{ animationPlayState: isEntryRevealAnimating ? "running" : "paused" }}
          aria-hidden="true"
        >
          <div className="absolute inset-0 bg-[radial-gradient(circle_at_center,_rgba(46,125,50,.52),_transparent_52%)]" />
          <img
            ref={entryRevealImageRef}
            src={devilIllustration}
            alt=""
            className="entry-reveal-devil relative h-auto w-[120vmin] max-w-none"
            style={{ animationPlayState: isEntryRevealAnimating ? "running" : "paused" }}
            onLoad={startEntryRevealAnimation}
            onError={startEntryRevealAnimation}
          />
        </div>
      )}
    </div>
  );
}
